import { CloudUploadOutlined, DeleteOutlined, DownloadOutlined, RedoOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Drawer, Form, Image, Input, Modal, Pagination, Row, Select, Space, Table, Tabs, Tag, Tooltip, message } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { batchDeletePublishContent, cancelPublishVerification, deletePublishContent, downloadPublishContent, getPublishAccountDetail, getPublishDetail, getPublishVerificationInfo, retryPublished, searchPublishList, submitPublishVerificationSms } from '../api';
import { API_BASE_URL } from '../api/baseUrl';
import { useAuth } from '../auth/AuthContext';
import { formatErrorMessage, resolveErrorMessage } from '../utils/errorMessage';
import SocialAccountLoginButton from './SocialAccountLoginButton';

const { RangePicker } = DatePicker;

const PublishedList = ({ contents, onEdit, onDelete, onPublish, onDownload, onFilter }) => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [detailForm] = Form.useForm();
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedTypeById, setSelectedTypeById] = useState({});
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [publishDetail, setPublishDetail] = useState(null);
  const [activeTab, setActiveTab] = useState('preview');
  const [smsVerifyVisible, setSmsVerifyVisible] = useState(false);
  const [smsVerifyLoading, setSmsVerifyLoading] = useState(false);
  const [smsVerifySubmitting, setSmsVerifySubmitting] = useState(false);
  const [smsVerifyInfo, setSmsVerifyInfo] = useState(null);
  const [smsVerifyCode, setSmsVerifyCode] = useState('');
  const [smsVerifyPublishId, setSmsVerifyPublishId] = useState('');
  const [autoLoginAccount, setAutoLoginAccount] = useState(null);
  const [autoLoginKey, setAutoLoginKey] = useState(0);
  const autoLoginTimerRef = useRef(null);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadForm] = Form.useForm();
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [accountNameById, setAccountNameById] = useState({});
  const accountFetchInFlightRef = useRef(new Set());
  const recentlyDeletedPublishIdsRef = useRef(new Map());
  // 发布账号抽屉相关状态
  const [publishAccountDrawerVisible, setPublishAccountDrawerVisible] = useState(false);
  const [accountLoading, setAccountLoading] = useState(false);
  const [accountDataSource, setAccountDataSource] = useState([]);
  const [accountPagination, setAccountPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const [currentPublishRecord, setCurrentPublishRecord] = useState(null);
  const [selectedAccountKeys, setSelectedAccountKeys] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  // 发布时间选择抽屉相关状态
  const [publishTimeDrawerVisible, setPublishTimeDrawerVisible] = useState(false);
  const [publishType, setPublishType] = useState('immediate'); // 'immediate' 或 'scheduled'
  const [publishTimeForm] = Form.useForm();
  // 发布账号筛选表单
  const [accountFilterForm] = Form.useForm();
  const [republishSubmittingById, setRepublishSubmittingById] = useState({});

  // 平台名称映射：中文 -> 英文代码
  const platformMap = {
    '微信公众号': 'wechat',
    '微信视频号': 'wechat_channels',
    '小红书': 'xhs',
    '抖音': 'douyin',
    '快手': 'kuaishou',
    '百家号': 'baijiahao',
    '知乎': 'zhihu',
    'B站': 'bilibili',
    '今日头条': 'toutiao',
  };

  // 平台代码反向映射：英文代码 -> 中文
  const platformNameMap = {
    'wechat': '微信公众号',
    'wechat_channels': '微信视频号',
    'weixin_channels': '微信视频号',
    'xhs': '小红书',
    'douyin': '抖音',
    'kuaishou': '快手',
    'baijiahao': '百家号',
    'zhihu': '知乎',
    'bilibili': 'B站',
    'toutiao': '今日头条',
  };

  // 状态映射：中文 -> 英文
  const statusMap = {
    '成功': 'success',
    '失败': 'failed',
    '待发布': 'pending',
    '发布中': 'publishing',
    '待验证码': 'verifying',
  };

  const renderWrapText = (value) => (
    <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
      {value ? String(value) : '-'}
    </div>
  );

  const SUMMARY_TOOLTIP_WIDTH = 800;
  const SUMMARY_TOOLTIP_MAX_WIDTH = 'min(800px, calc(100vw - 24px))';
  const SUMMARY_TOOLTIP_MAX_HEIGHT = '60vh';

  const renderSummaryWithTooltip = (value) => {
    const text = value ? String(value) : '';
    if (!text) return '-';
    const needsTruncate = text.length > 100;
    const display = needsTruncate ? `${text.slice(0, 100)}…` : text;
    const content = (
      <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
        {display}
      </div>
    );
    if (!needsTruncate) return content;
    return (
      <Tooltip
        title={<div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{text}</div>}
        placement="top"
        arrow={{ pointAtCenter: true }}
        autoAdjustOverflow={{ adjustX: 1, adjustY: 1 }}
        styles={{
          container: {
            width: SUMMARY_TOOLTIP_WIDTH,
            maxWidth: SUMMARY_TOOLTIP_MAX_WIDTH,
            maxHeight: SUMMARY_TOOLTIP_MAX_HEIGHT,
            overflowY: 'auto',
          },
        }}
        overlayInnerStyle={{
          width: SUMMARY_TOOLTIP_WIDTH,
          maxWidth: SUMMARY_TOOLTIP_MAX_WIDTH,
          maxHeight: SUMMARY_TOOLTIP_MAX_HEIGHT,
          overflowY: 'auto',
        }}
      >
        {content}
      </Tooltip>
    );
  };

  const isVideoType = (value) => {
    if (!value) return false;
    const text = String(value).trim();
    const normalized = text.toLowerCase();
    return text === '视频' || normalized === 'video';
  };

  // 构建筛选参数
  const buildSearchParams = (formValues = {}, page = 1, pageSize = 10) => {
    const params = {
      page,
      page_size: pageSize,
      keyword: formValues.keyword || '',
      filters: {},
      sort: { field: 'publish_time', order: 'desc' },
    };

    // 处理内容类型筛选 - 传入 filters.template_type
    if (formValues.template_type) {
      params.filters.template_type = formValues.template_type;
    }

    // 处理标题筛选 - 传入 filters.title
    if (formValues.title && formValues.title.trim()) {
      params.filters.title = formValues.title.trim();
    }

    if (formValues.publisher_user_name && formValues.publisher_user_name.trim()) {
      params.filters.publisher_user_name = formValues.publisher_user_name.trim();
    }

    if (formValues.account_name_keyword && formValues.account_name_keyword.trim()) {
      params.filters.account_name_keyword = formValues.account_name_keyword.trim();
    }

    // 处理平台筛选
    if (formValues.available_platforms && formValues.available_platforms.length > 0) {
      params.filters.platforms = formValues.available_platforms.map(p => platformMap[p] || p);
    }

    // 处理状态筛选
    if (formValues.status && formValues.status.length > 0) {
      params.filters.status = formValues.status.map(s => statusMap[s] || s);
    }

    // 如果 filters 为空对象，可以删除它（可选，根据接口要求）
    if (Object.keys(params.filters).length === 0) {
      delete params.filters;
    }

    return params;
  };

  // 加载数据
  const loadData = async (searchParams = {}, page = null, pageSize = null) => {
    setLoading(true);
    try {
      const currentPage = page !== null ? page : pagination.current;
      const currentPageSize = pageSize !== null ? pageSize : pagination.pageSize;

      const params = {
        ...searchParams,
        page: currentPage,
        page_size: currentPageSize,
      };

      const response = await searchPublishList(params);

      if (response.status === 'success') {
        const ttlMs = 8000;
        const now = Date.now();
        for (const [id, ts] of recentlyDeletedPublishIdsRef.current.entries()) {
          if (!ts || now - ts > ttlMs) recentlyDeletedPublishIdsRef.current.delete(id);
        }
        const items = Array.isArray(response.items) ? response.items : [];
        const filtered = recentlyDeletedPublishIdsRef.current.size > 0
          ? items.filter((it) => !recentlyDeletedPublishIdsRef.current.has(it?.publish_id))
          : items;

        setDataSource(filtered);
        setPagination({
          current: response.page || currentPage,
          pageSize: response.page_size || currentPageSize,
          total: response.total || 0,
        });
      }
    } catch (error) {
      console.error('加载数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 初始加载数据
  useEffect(() => {
    loadData({});
  }, []);

  const normalizeTemplateType = (raw) => {
    const t = String(raw || '').trim();
    if (!t) return '';
    const normalized = t.toLowerCase();
    if (normalized === 'video') return '视频';
    if (normalized === 'image_text' || normalized === 'imagetext' || normalized === 'article') return '图文';
    return t;
  };

  const selectedRowKeySet = useMemo(() => new Set(selectedRowKeys || []), [selectedRowKeys]);

  // 当翻页/检索导致 dataSource 更新时，补全当前页已选中项的类型信息（跨页选择需要）
  useEffect(() => {
    if (!dataSource || dataSource.length === 0) return;
    if (!selectedRowKeys || selectedRowKeys.length === 0) return;

    setSelectedTypeById((prev) => {
      let changed = false;
      const next = { ...(prev || {}) };
      for (const row of dataSource) {
        const id = row?.publish_id;
        if (!id) continue;
        if (!selectedRowKeySet.has(id)) continue;
        const t = normalizeTemplateType(row?.template_type);
        if (t && next[id] !== t) {
          next[id] = t;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [dataSource, selectedRowKeySet, selectedRowKeys]);

  // 选中项清空时，清理类型缓存，避免残留影响按钮显示
  useEffect(() => {
    if ((selectedRowKeys || []).length === 0) {
      setSelectedTypeById({});
    }
  }, [selectedRowKeys]);

  // 兜底补全账号名：如果 publish/search 未返回 account_name，则按本页出现的 account_id 拉取账号详情
  useEffect(() => {
    const idsToFetch = Array.from(
      new Set(
        (dataSource || [])
          .filter((item) => item?.account_id && !item?.account_name && !accountNameById[item.account_id])
          .map((item) => item.account_id)
      )
    ).filter((id) => !accountFetchInFlightRef.current.has(id));

    if (idsToFetch.length === 0) return;

    idsToFetch.forEach((id) => accountFetchInFlightRef.current.add(id));

    let cancelled = false;
    (async () => {
      const next = {};

      for (const accountId of idsToFetch) {
        try {
          const response = await getPublishAccountDetail(accountId);
          if (response?.status === 'success' && response?.account?.account_name) {
            next[accountId] = response.account.account_name;
          }
        } catch (error) {
          // ignore：保持表格可用，账号名显示为 '-'
        } finally {
          accountFetchInFlightRef.current.delete(accountId);
        }
      }

      if (!cancelled && Object.keys(next).length > 0) {
        setAccountNameById((prev) => ({ ...prev, ...next }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accountNameById, dataSource]);

  // 兜底补全详情页账号名：publish/detail 可能仅返回 account_id
  useEffect(() => {
    const accountId = publishDetail?.account_id;
    if (!accountId) return;
    if (publishDetail?.account_name) return;
    if (accountNameById[accountId]) return;
    if (accountFetchInFlightRef.current.has(accountId)) return;

    accountFetchInFlightRef.current.add(accountId);
    let cancelled = false;
    (async () => {
      try {
        const response = await getPublishAccountDetail(accountId);
        const accountName = response?.status === 'success' ? response?.account?.account_name : null;
        if (!accountName) return;
        if (cancelled) return;
        setAccountNameById((prev) => ({ ...prev, [accountId]: accountName }));
        setPublishDetail((prev) => {
          if (!prev || prev.account_id !== accountId) return prev;
          return { ...prev, account_name: accountName };
        });
      } catch (_) {
        // ignore
      } finally {
        accountFetchInFlightRef.current.delete(accountId);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [accountNameById, publishDetail]);

  const currentPagePublishIds = (dataSource || [])
    .map((item) => item.publish_id)
    .filter(Boolean);

  const isAllCurrentPageSelected = currentPagePublishIds.length > 0
    && currentPagePublishIds.every((id) => selectedRowKeys.includes(id));

  const toggleSelectAllCurrentPage = () => {
    if (currentPagePublishIds.length === 0) return;

    setSelectedRowKeys((prevKeys) => {
      const otherPageSelected = (prevKeys || []).filter((key) => !currentPagePublishIds.includes(key));
      const prevAllCurrent = currentPagePublishIds.every((id) => (prevKeys || []).includes(id));
      const nextKeys = prevAllCurrent
        ? otherPageSelected
        : Array.from(new Set([...otherPageSelected, ...currentPagePublishIds]));

      setSelectedTypeById((prev) => {
        const next = {};
        for (const k of nextKeys) {
          if (prev && prev[k]) next[k] = prev[k];
        }
        // 如果是“全选本页”，补全本页所有内容的类型
        if (!prevAllCurrent) {
          for (const row of dataSource || []) {
            const id = row?.publish_id;
            if (!id) continue;
            if (!nextKeys.includes(id)) continue;
            const t = normalizeTemplateType(row?.template_type);
            if (t) next[id] = t;
          }
        }
        return next;
      });

      return nextKeys;
    });
  };

  const markPublishIdsDeleted = (publishIds = []) => {
    const now = Date.now();
    const ids = Array.from(new Set((publishIds || []).filter(Boolean)));
    ids.forEach((id) => recentlyDeletedPublishIdsRef.current.set(id, now));
  };

  const reloadListAfterDelete = async (deletedPublishIds = []) => {
    const ids = Array.from(new Set((deletedPublishIds || []).filter(Boolean)));
    const currentPageIds = (dataSource || []).map((item) => item?.publish_id).filter(Boolean);
    const deletedOnCurrentPage = ids.filter((id) => currentPageIds.includes(id)).length;
    const shouldBackPage = pagination.current > 1 && currentPageIds.length > 0 && deletedOnCurrentPage >= currentPageIds.length;
    const nextPage = shouldBackPage ? pagination.current - 1 : pagination.current;

    const formValues = form.getFieldsValue();
    const searchParams = buildSearchParams(formValues, nextPage, pagination.pageSize);
    await loadData(searchParams, nextPage, pagination.pageSize);
  };

  // 格式化时间戳
  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-';
    try {
      const date = new Date(dateTimeString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      return dateTimeString;
    }
  };



  // 处理检索
  const handleSearch = () => {
    const formValues = form.getFieldsValue();
    const searchParams = buildSearchParams(formValues, 1, pagination.pageSize);
    loadData(searchParams, 1, pagination.pageSize);
  };

  // 处理筛选
  const handleFilterFinish = (values) => {
    const searchParams = buildSearchParams(values, 1, pagination.pageSize);
    loadData(searchParams, 1, pagination.pageSize);
  };

  // 处理查看按钮点击
  const handleView = async (record) => {
    setDrawerVisible(true);
    setDetailLoading(true);
    setActiveTab('preview');
    try {
      const response = await getPublishDetail({ publish_id: record.publish_id, include_content: true });
      if (response.status === 'success' && response.data && response.data.publish) {
        const publish = response.data.publish || {};
        const content = response.data.content || null;
        const body = content?.body;
        setPublishDetail({
          ...publish,
          ...(body ? { body } : {}),
        });
        if (isLoginFailureCode(publish?.error_code, publish?.error_message)) {
          const loginAccount = buildSocialLoginAccount(publish);
          triggerAutoLogin(loginAccount, 260);
        }
      } else {
        message.error('获取发布详情失败');
        setDrawerVisible(false);
      }
    } catch (error) {
      console.error('获取发布详情失败:', error);
      message.error('获取发布详情失败，请稍后重试');
      setDrawerVisible(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const reloadListKeepCurrent = async () => {
    const formValues = form.getFieldsValue();
    const searchParams = buildSearchParams(formValues, pagination.current, pagination.pageSize);
    await loadData(searchParams, pagination.current, pagination.pageSize);
  };

  const buildScreenshotUrl = (url) => {
    if (!url) return '';
    if (String(url).startsWith('http')) return url;
    const base = String(API_BASE_URL || '').replace(/\/+$/, '');
    const path = String(url).replace(/^\/+/, '');
    return `${base}/${path}`;
  };

  const buildSocialLoginAccount = (record) => {
    if (!record) return null;
    let platformCode = record?.platform_code
      || platformMap[record?.platform]
      || platformMap[record?.platform_display]
      || record?.platform;
    if (platformCode === 'wechat_channels') platformCode = 'weixin_channels';
    return {
      account_id: record?.account_id,
      account_name: record?.account_name || accountNameById?.[record?.account_id],
      platform_code: platformCode,
      platform: record?.platform,
    };
  };

  const isLoginFailureCode = (code, msg = '') => {
    const c = String(code || '').trim();
    if ([
      'DOUYIN_NOT_LOGGED_IN',
      'KS_NOT_LOGGED_IN',
      'WXC_NOT_LOGGED_IN',
      'DOUYIN_COOKIE_MISSING',
      'KS_COOKIE_MISSING',
      'WXC_COOKIE_MISSING',
    ].includes(c)) return true;
    const m = String(msg || '');
    if (!m) return false;
    return /NOT_LOGGED_IN|cookie_invalid|cookie\s*失效|未登录|请扫码|扫码登录|登录失效/i.test(m);
  };

  const clearAutoLoginTimer = () => {
    if (autoLoginTimerRef.current) {
      clearTimeout(autoLoginTimerRef.current);
      autoLoginTimerRef.current = null;
    }
  };

  const triggerAutoLogin = (loginAccount, delayMs = 200) => {
    if (!loginAccount?.account_id) return;
    clearAutoLoginTimer();
    autoLoginTimerRef.current = setTimeout(() => {
      setAutoLoginAccount(loginAccount);
      setAutoLoginKey((k) => k + 1);
    }, delayMs);
  };

  useEffect(() => () => clearAutoLoginTimer(), []);

  const openSmsVerifyModal = async (record) => {
    const publishId = record?.publish_id;
    if (!publishId) {
      message.error('缺少 publish_id，无法进行验证码校验');
      return;
    }
    setSmsVerifyVisible(true);
    setSmsVerifyLoading(true);
    setSmsVerifyInfo(null);
    setSmsVerifyCode('');
    setSmsVerifyPublishId(publishId);
    try {
      const resp = await getPublishVerificationInfo({ publish_id: publishId });
      if (resp?.status === 'success') {
        setSmsVerifyInfo(resp);
      } else {
        const errMsg = resolveErrorMessage(resp?.error_message || resp?.message, '获取验证码信息失败');
        message.error(errMsg);
      }
    } catch (error) {
      message.error(error?.message || '获取验证码信息失败，请稍后重试');
    } finally {
      setSmsVerifyLoading(false);
    }
  };

  const handleSubmitSmsVerify = async () => {
    const publishId = smsVerifyPublishId;
    const code = String(smsVerifyCode || '').trim();
    if (!publishId) {
      message.error('缺少 publish_id');
      return;
    }
    if (!code) {
      message.warning('请输入短信验证码');
      return;
    }
    try {
      setSmsVerifySubmitting(true);
      const resp = await submitPublishVerificationSms({ publish_id: publishId, sms_code: code });
      if (resp?.status !== 'success') {
        const errMsg = resolveErrorMessage(resp?.error_message || resp?.message, '提交验证码失败');
        message.error(errMsg);
        return;
      }
      message.success('验证码已提交，正在继续发布');
      setSmsVerifyVisible(false);
      setSmsVerifyInfo(null);
      setSmsVerifyCode('');
      setSmsVerifyPublishId('');
      await new Promise((r) => setTimeout(r, 300));
      await reloadListKeepCurrent();
    } catch (error) {
      message.error(error?.message || '提交验证码失败，请稍后重试');
    } finally {
      setSmsVerifySubmitting(false);
    }
  };

  const handleCancelSmsVerify = async (record) => {
    const publishId = record?.publish_id || smsVerifyPublishId;
    if (!publishId) {
      message.error('缺少 publish_id');
      return;
    }
    try {
      setSmsVerifySubmitting(true);
      const resp = await cancelPublishVerification({ publish_id: publishId });
      if (resp?.status !== 'success') {
        const errMsg = resolveErrorMessage(resp?.error_message || resp?.message, '取消验证码失败');
        message.error(errMsg);
        return;
      }
      message.success('已取消验证码，可重新发布');
      setSmsVerifyVisible(false);
      setSmsVerifyInfo(null);
      setSmsVerifyCode('');
      setSmsVerifyPublishId('');
      await new Promise((r) => setTimeout(r, 300));
      await reloadListKeepCurrent();
    } catch (error) {
      message.error(error?.message || '取消验证码失败，请稍后重试');
    } finally {
      setSmsVerifySubmitting(false);
    }
  };

  const handleRetryPublish = async (record) => {
    if (!record) return;
    if (String(record?.status || '').toLowerCase() !== 'failed') {
      message.warning('仅支持对“失败”的发布记录重新发布');
      return;
    }
    const publishId = record?.publish_id;
    if (!publishId) {
      message.error('缺少 publish_id，无法重新发布');
      return;
    }
    if (republishSubmittingById?.[publishId]) return;

    try {
      const creatorUserId = user?.user_id || user?.id || record?.publisher_user_id || 'system';
      setRepublishSubmittingById((prev) => ({ ...(prev || {}), [publishId]: true }));
      message.loading({ content: '正在重新发布…', key: `republish_${publishId}`, duration: 0 });

      // optimistic update current row
      setDataSource((prev) => (prev || []).map((it) => (
        it?.publish_id === publishId ? { ...it, status: 'publishing', publish_time: new Date().toISOString() } : it
      )));

      const resp = await retryPublished({
        publish_id: publishId,
        publisher_user_id: creatorUserId,
        override: { allow_sms_verify: true },
      });
      if (resp?.status !== 'success') {
        const errMsg = resolveErrorMessage(resp?.error_message || resp?.message, '重新发布失败');
        message.error({ content: errMsg, key: `republish_${publishId}` });
        if (isLoginFailureCode(resp?.error_code, resp?.error_message || resp?.message)) {
          const loginAccount = buildSocialLoginAccount(record);
          triggerAutoLogin(loginAccount, 260);
        }
        await reloadListKeepCurrent();
        return;
      }

      if (String(resp?.publish_status || '').toLowerCase() === 'failed'
        && isLoginFailureCode(resp?.error_code, resp?.error_message)) {
        const loginAccount = buildSocialLoginAccount(record);
        triggerAutoLogin(loginAccount, 260);
      }

      message.success({ content: '已提交重新发布，正在打开发布详情…', key: `republish_${publishId}`, duration: 2 });
      await new Promise((r) => setTimeout(r, 250));
      await handleView({ publish_id: publishId });
      // 刷新列表，展示最终状态（后端可能异步落库/更新）
      setTimeout(() => { reloadListKeepCurrent().catch(() => {}); }, 800);
    } catch (e) {
      message.error({ content: e?.message || String(e), key: `republish_${publishId || 'unknown'}` });
    } finally {
      if (publishId) {
        setRepublishSubmittingById((prev) => ({ ...(prev || {}), [publishId]: false }));
      }
    }
  };

  // 处理单项下载
  const handleDownload = async (record) => {
    try {
      await downloadPublishContent(record.publish_id);
      message.success('下载成功');
    } catch (error) {
      console.error('下载失败:', error);
      message.error('下载失败，请稍后重试');
    }
  };

  // 处理单项删除
  const handleDelete = async (record) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这条发布记录吗？此操作不可恢复。',
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await deletePublishContent(record.publish_id);
          if (response.status === 'success') {
            message.success('删除成功');
            markPublishIdsDeleted([record.publish_id]);
            setSelectedRowKeys((prev) => (prev || []).filter((id) => id !== record.publish_id));
            setDataSource((prev) => (prev || []).filter((it) => it?.publish_id !== record.publish_id));
            await new Promise((r) => setTimeout(r, 300));
            await reloadListAfterDelete([record.publish_id]);
          } else {
            message.error('删除失败');
          }
        } catch (error) {
          console.error('删除失败:', error);
          message.error('删除失败，请稍后重试');
        }
      },
    });
  };

  // 处理批量删除
  const handleBatchDelete = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的内容');
      return;
    }

    const publishIds = [...selectedRowKeys];
    Modal.confirm({
      title: '确认批量删除',
      content: `确定要删除选中的 ${selectedRowKeys.length} 条记录吗？此操作不可恢复。`,
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          message.loading({ content: `正在删除 ${selectedRowKeys.length} 条记录...`, key: 'batchDelete' });
          const result = await batchDeletePublishContent(publishIds);
          const deletedIds = Array.isArray(result?.deleted_ids)
            ? result.deleted_ids
            : (result?.status === 'success' ? publishIds : []);
          const notFoundIds = Array.isArray(result?.not_found_ids) ? result.not_found_ids : [];
          const failedIds = Array.isArray(result?.failed_ids) ? result.failed_ids : [];

          const deletedCount = deletedIds.length;
          const notFoundCount = notFoundIds.length;
          const failedCount = failedIds.length;

          // 清空选中项
          setSelectedRowKeys([]);
          markPublishIdsDeleted(publishIds);
          setDataSource((prev) => (prev || []).filter((it) => !publishIds.includes(it?.publish_id)));

          if (failedCount === 0 && notFoundCount === 0) {
            message.success({ content: `成功删除 ${deletedCount} 条记录`, key: 'batchDelete' });
          } else if (notFoundCount > 0 && failedCount === 0) {
            message.warning({
              content: `删除完成：成功 ${deletedCount} 条，未找到 ${notFoundCount} 条`,
              key: 'batchDelete',
              duration: 5
            });
          } else {
            message.warning({
              content: `删除完成：成功 ${deletedCount} 条，未找到 ${notFoundCount} 条，失败 ${failedCount} 条`,
              key: 'batchDelete',
              duration: 5
            });
          }

          // 重新加载数据
          await new Promise((r) => setTimeout(r, 300));
          await reloadListAfterDelete(publishIds);
        } catch (error) {
          console.error('批量删除失败:', error);
          message.error({ content: '批量删除失败，请稍后重试', key: 'batchDelete' });
        }
      },
    });
  };

  // 处理批量下载
  const handleBatchDownload = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要下载的内容');
      return;
    }

    try {
      message.loading({ content: `正在下载 ${selectedRowKeys.length} 个文件...`, key: 'batchDownload' });
      
      // 逐个下载，避免浏览器阻止多个下载
      for (let i = 0; i < selectedRowKeys.length; i++) {
        const publishId = selectedRowKeys[i];
        try {
          await downloadPublishContent(publishId);
          // 添加延迟，避免浏览器阻止多个下载
          if (i < selectedRowKeys.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        } catch (error) {
          console.error(`下载 ${publishId} 失败:`, error);
        }
      }
      
      message.success({ content: `成功下载 ${selectedRowKeys.length} 个文件`, key: 'batchDownload' });
    } catch (error) {
      console.error('批量下载失败:', error);
      message.error({ content: '批量下载失败，请稍后重试', key: 'batchDownload' });
    }
  };

  const columns = [
    {
      title: '发布id',
      dataIndex: 'publish_id',
      key: 'publish_id',
      width: 120,
    },
    {
      title: '内容id',
      dataIndex: 'content_id',
      key: 'content_id',
      width: 120,
    },
    {
      title: '平台',
      dataIndex: 'platform',
      key: 'platform',
      width: 100,
      render: (platform) => platformNameMap[platform] || platform,
    },
    {
      title: '内容类型',
      dataIndex: 'template_type',
      key: 'template_type',
      width: 100,
      render: (templateType) => {
        if (!templateType) return '-';
        const typeText = String(templateType);
        const normalized = typeText.toLowerCase();

        // 兼容少量英文/别名（如有）
        const mapped =
          normalized === 'video' ? '视频'
            : (normalized === 'image_text' || normalized === 'imagetext' || normalized === 'article') ? '图文'
              : typeText;

        return <span style={{ color: '#333' }}>{mapped}</span>;
      },
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const statusColors = {
          'success': 'green',
          'failed': 'red',
          'pending': 'orange',
          'publishing': 'blue',
          'verifying': 'gold',
        };
        const statusNames = {
          'success': '成功',
          'failed': '失败',
          'pending': '待发布',
          'publishing': '发布中',
          'verifying': '待验证码',
        };
        return <Tag color={statusColors[status]}>{statusNames[status] || status}</Tag>;
      },
    },
    {
      title: '发布时间',
      dataIndex: 'publish_time',
      key: 'publish_time',
      width: 180,
      render: (text) => formatDateTime(text),
      sorter: (a, b) => {
        if (!a.publish_time || !b.publish_time) return 0;
        return new Date(a.publish_time) - new Date(b.publish_time);
      },
    },
    {
      title: '发布者',
      dataIndex: 'publisher_user_name',
      key: 'publisher_user_name',
      width: 120,
      render: (value) => (value ? String(value) : '-'),
    },
    {
      title: '账号ID',
      dataIndex: 'account_id',
      key: 'account_id',
      width: 140,
      onCell: () => ({
        style: { whiteSpace: 'normal', wordBreak: 'break-all' },
      }),
      render: (account_id) => {
        const text = account_id ? String(account_id).trim() : '';
        if (!text) return '-';
        return (
          <div title={text} style={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>
            {text}
          </div>
        );
      },
    },
    {
      title: '账号名',
      dataIndex: 'account_name',
      key: 'account_name',
      width: 180,
      onCell: () => ({
        style: { whiteSpace: 'normal', wordBreak: 'break-word' },
      }),
      render: (account_name, record) => {
        const name = account_name || accountNameById[record?.account_id];
        const text = name ? String(name).trim() : '';
        if (!text) return '-';
        return (
          <div title={text} style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
            {text}
          </div>
        );
      },
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      width: 200,
      render: renderWrapText,
    },
    {
      title: '内容简介',
      dataIndex: 'summary',
      key: 'summary',
      width: 260,
      render: renderSummaryWithTooltip,
    },
    {
      title: '标签',
      dataIndex: 'tags',
      key: 'tags',
      width: 150,
      render: (tags) => {
        if (!tags || !Array.isArray(tags)) return '-';
        return (
          <Space size={[0, 8]} wrap>
            {tags.map((tag, index) => (
              <Tag key={index}>{tag}</Tag>
            ))}
          </Space>
        );
      },
    },
    {
      title: '数据',
      key: 'stats',
      width: 150,
      render: (_, record) => (
        <div style={{ fontSize: '12px' }}>
          <div>浏览: {record.view_count || 0}</div>
          <div>点赞: {record.like_count || 0}</div>
          <div>评论: {record.comment_count || 0}</div>
        </div>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 320,
      fixed: 'right',
      render: (_, record) => {
        const statusLower = String(record?.status || '').toLowerCase();
        const showSmsVerify = statusLower === 'verifying' || String(record?.error_code || '') === 'SMS_VERIFICATION_REQUIRED';
        const errorCode = String(record?.error_code || '').trim();
        const needLogin = [
          'DOUYIN_NOT_LOGGED_IN',
          'KS_NOT_LOGGED_IN',
          'WXC_NOT_LOGGED_IN',
          'DOUYIN_COOKIE_MISSING',
          'KS_COOKIE_MISSING',
          'WXC_COOKIE_MISSING',
        ].includes(errorCode);
        let platformCode = record?.platform_code
          || platformMap[record?.platform]
          || platformMap[record?.platform_display]
          || record?.platform;
        if (platformCode === 'wechat_channels') platformCode = 'weixin_channels';
        const loginAccount = {
          account_id: record?.account_id,
          account_name: record?.account_name,
          platform_code: platformCode,
          platform: record?.platform,
        };
        return (
          <Space size="small" style={{ display: 'flex', flexWrap: 'nowrap' }}>
            <Button
              type="link"
              size="small"
              icon={<CloudUploadOutlined />}
              onClick={() => handleView(record)}
              style={{ padding: '0 4px' }}
            >
              查看
            </Button>
            {['failed', 'publishing'].includes(statusLower) && (
              <Button
                type="link"
                size="small"
                icon={<RedoOutlined />}
                onClick={() => handleRetryPublish(record)}
                disabled={statusLower !== 'failed' || !!republishSubmittingById?.[record?.publish_id]}
                style={{ padding: '0 4px' }}
              >
                重新发布
              </Button>
            )}
            {showSmsVerify && (
              <Button
                type="link"
                size="small"
                onClick={() => openSmsVerifyModal(record)}
                style={{ padding: '0 4px' }}
              >
                验证码
              </Button>
            )}
            {isVideoType(record.template_type) && (
              <Button
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(record)}
                style={{ padding: '0 4px' }}
              >
                下载
              </Button>
            )}
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
              style={{ padding: '0 4px' }}
            >
              删除
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div className="page-panel">
      <div className="page-panel__top">
        <Form
          form={form}
          layout="horizontal"
          onFinish={handleFilterFinish}
          style={{ marginBottom: 0 }}
        >
        <Row gutter={[16, 16]} justify="space-between">
          <Col span={18}>
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Form.Item label="关键词" name="keyword" style={{ marginBottom: 0 }}>
                  <Input placeholder="匹配标题/摘要" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="标题" name="title" style={{ marginBottom: 0 }}>
                  <Input placeholder="请输入标题" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="内容ID" name="content_id" style={{ marginBottom: 0 }}>
                  <Input placeholder="请输入内容ID" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="发布ID" name="publish_id" style={{ marginBottom: 0 }}>
                  <Input placeholder="请输入发布ID" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="发布者" name="publisher_user_name" style={{ marginBottom: 0 }}>
                  <Input placeholder="请输入发布者名称" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="账号名" name="account_name_keyword" style={{ marginBottom: 0 }}>
                  <Input placeholder="请输入账号名（模糊匹配）" style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col span={6}>
                <Form.Item label="投递平台" name="available_platforms" style={{ marginBottom: 0 }}>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    placeholder="请选择平台"
                    options={[
                      { label: '微信公众号', value: '微信公众号' },
                      { label: '微信视频号', value: '微信视频号' },
                      { label: '小红书', value: '小红书' },
                      { label: '抖音', value: '抖音' },
                      { label: '快手', value: '快手' },
                      { label: '百家号', value: '百家号' },
                      { label: '知乎', value: '知乎' },
                      { label: 'B站', value: 'B站' },
                      { label: '今日头条', value: '今日头条' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="发布状态" name="status" style={{ marginBottom: 0 }}>
                  <Select
                    mode="multiple"
                    allowClear
                    style={{ width: '100%' }}
                    placeholder="请选择状态"
                    options={[
                      { label: '成功', value: '成功' },
                      { label: '失败', value: '失败' },
                      { label: '待发布', value: '待发布' },
                      { label: '发布中', value: '发布中' },
                      { label: '待验证码', value: '待验证码' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="内容类型" name="template_type" style={{ marginBottom: 0 }}>
                  <Select
                    allowClear
                    style={{ width: '100%' }}
                    placeholder="请选择类型"
                    options={[
                      { label: '视频', value: '视频' },
                      { label: '图文', value: '图文' },
                      { label: '笔记', value: '笔记' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item label="发布时间" name="publish_time_range" style={{ marginBottom: 0 }}>
                  <RangePicker
                    style={{ width: '100%', minWidth: 0 }}
                    showTime={{ format: 'HH:mm:ss' }}
                    format="YYYY-MM-DD HH:mm:ss"
                    placeholder={['开始时间', '结束时间']}
                  />
                </Form.Item>
              </Col>

            </Row>
          </Col>

          <Col span={6} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Space direction="vertical" size="small" style={{ width: '100%', alignItems: 'center' }}>
              <Space size="small" style={{ width: 200, justifyContent: 'space-between' }}>
                <Button type="primary" style={{ width: 80 }} onClick={handleSearch}>
                  检索
                </Button>
              </Space>
            </Space>
          </Col>
        </Row>
        </Form>
      </div>

      <div className="page-panel__body">
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="publish_id"
          loading={loading}
          scroll={{ x: 2200 }}
          rowSelection={{
            selectedRowKeys: selectedRowKeys.filter(key =>
              // 显示当前页的选中项，以及之前在其他页选中的项
              dataSource.some(item => item.publish_id === key) ||
              !dataSource.some(item => item.publish_id === key)
            ),
            onChange: (keys, rows) => {
              // 获取当前页的所有 publish_id
              const currentPageIds = dataSource.map(item => item.publish_id);

              // 获取之前在其他页选中的项（不在当前页的）
              const otherPageSelected = selectedRowKeys.filter(
                key => !currentPageIds.includes(key)
              );

              // 合并：其他页已选中的项 + 当前页新选中的项
              const allSelectedKeys = [...otherPageSelected, ...keys];

              // 去重
              const uniqueKeys = Array.from(new Set(allSelectedKeys));

              setSelectedRowKeys(uniqueKeys);

              // 更新类型缓存：保留其他页已知类型 + 用当前页 rows 补全
              setSelectedTypeById((prev) => {
                const next = {};
                for (const k of uniqueKeys) {
                  if (prev && prev[k]) next[k] = prev[k];
                }
                for (const row of rows || []) {
                  const id = row?.publish_id;
                  if (!id) continue;
                  const t = normalizeTemplateType(row?.template_type);
                  if (t) next[id] = t;
                }
                return next;
              });
            },
          }}
          pagination={false}
        />
      </div>

      <div className="page-panel__bottom" style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px 0',
        background: '#fff',
      }}>
        {/* 左侧：批量操作按钮 */}
        <div>
          {selectedRowKeys.length > 0 && (
            <Space>
              <Button onClick={toggleSelectAllCurrentPage}>
                {isAllCurrentPageSelected ? '取消全选(本页)' : '全选本页'}
              </Button>
              <span>已选择 {selectedRowKeys.length} 项</span>
              {!selectedRowKeys.some((id) => {
                const t = normalizeTemplateType(selectedTypeById?.[id]);
                return t === '图文' || t === '笔记';
              }) && (
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleBatchDownload}
                >
                  批量下载
                </Button>
              )}
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleBatchDelete}
              >
                批量删除
              </Button>
              <Button
                onClick={() => setSelectedRowKeys([])}
              >
                清空全部
              </Button>
            </Space>
          )}
        </div>

        {/* 右侧：分页器 */}
        <div>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showTotal={(total) => `共 ${total} 条记录`}
            showSizeChanger
            showQuickJumper
            style={{ margin: 0 }}
            onChange={(page, pageSize) => {
              const formValues = form.getFieldsValue();
              const searchParams = buildSearchParams(formValues, page, pageSize);
              loadData(searchParams, page, pageSize);
            }}
            onShowSizeChange={(current, size) => {
              const formValues = form.getFieldsValue();
              const searchParams = buildSearchParams(formValues, 1, size);
              loadData(searchParams, 1, size);
            }}
          />
        </div>
      </div>

      {/* 发布详情抽屉 */}
      <Drawer
        title="发布详情"
        placement="right"
        width={700}
        onClose={() => {
          setDrawerVisible(false);
          setPublishDetail(null);
        }}
        open={drawerVisible}
        loading={detailLoading}
        mask={true}
        maskStyle={{ backdropFilter: 'none', WebkitBackdropFilter: 'none' }}
      >
        {publishDetail && (
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            items={[
              {
                key: 'preview',
                label: '内容预览',
                children: (
                  <div style={{ padding: '16px 0' }}>
                    <div style={{ marginBottom: 24 }}>
                      <h3 style={{ marginBottom: 8, fontSize: 18, fontWeight: 600 }}>标题</h3>
                      <p style={{ fontSize: 16, color: '#333' }}>{publishDetail.title}</p>
                    </div>
                    <div style={{ marginBottom: 24 }}>
                      <h3 style={{ marginBottom: 8, fontSize: 18, fontWeight: 600 }}>内容简介</h3>
                      <p style={{ fontSize: 14, color: '#666', lineHeight: 1.8 }}>{publishDetail.summary || '-'}</p>
                    </div>
                    {publishDetail.tags && publishDetail.tags.length > 0 && (
                      <div style={{ marginBottom: 24 }}>
                        <h3 style={{ marginBottom: 8, fontSize: 18, fontWeight: 600 }}>标签</h3>
                        <Space size={[0, 8]} wrap>
                          {publishDetail.tags.map((tag, index) => (
                            <Tag key={index}>{tag}</Tag>
                          ))}
                        </Space>
                      </div>
                    )}
                    {publishDetail.media_urls && publishDetail.media_urls.length > 0 && (
                      <div style={{ marginBottom: 24 }}>
                        <h3 style={{ marginBottom: 8, fontSize: 18, fontWeight: 600 }}>
                          {(() => {
                            const p = String(publishDetail?.platform || '').trim();
                            const isXhs = p === 'xhs' || p === '小红书';
                            return isXhs ? '内容图片' : '媒体文件';
                          })()}
                        </h3>
                        <Image.PreviewGroup>
                          {(() => {
                            const urls = Array.isArray(publishDetail.media_urls) ? publishDetail.media_urls : [];
                            const p = String(publishDetail?.platform || '').trim();
                            const isXhs = p === 'xhs' || p === '小红书';

                            const isVideoUrl = (u) => (
                              /\.(mp4|avi|mov|wmv|flv|webm|m3u8)$/i.test(u) || /video|mp4|avi|mov/i.test(u)
                            );
                            const isImageUrl = (u) => (
                              /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(u) || /image|jpg|jpeg|png|gif/i.test(u)
                            );

                            // For XHS notes, thumbnails in a 4-column grid to reduce vertical space.
                            if (isXhs) {
                              const imageUrls = urls.filter((u) => isImageUrl(String(u || '')));
                              const videoUrls = urls.filter((u) => isVideoUrl(String(u || '')));
                              const otherUrls = urls.filter((u) => !isImageUrl(String(u || '')) && !isVideoUrl(String(u || '')));

                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                  {imageUrls.length > 0 && (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
                                      {imageUrls.map((url, index) => (
                                        <Image
                                          key={`img_${index}`}
                                          src={url}
                                          alt={`图片 ${index + 1}`}
                                          style={{
                                            width: '100%',
                                            height: 140,
                                            borderRadius: 8,
                                            objectFit: 'cover',
                                            background: '#fafafa',
                                          }}
                                          preview={{ mask: '预览' }}
                                        />
                                      ))}
                                    </div>
                                  )}

                                  {videoUrls.map((url, index) => (
                                    <div key={`video_${index}`} style={{ width: '100%' }}>
                                      <div style={{ position: 'relative', width: '100%', maxWidth: 600 }}>
                                        <video
                                          src={url}
                                          controls
                                          style={{
                                            width: '100%',
                                            maxHeight: '400px',
                                            borderRadius: '8px',
                                            backgroundColor: '#000',
                                          }}
                                          onError={(e) => {
                                            console.error('视频加载失败:', url);
                                            e.target.style.display = 'none';
                                            e.target.nextSibling.style.display = 'block';
                                          }}
                                        />
                                        <div style={{
                                          display: 'none',
                                          padding: '20px',
                                          textAlign: 'center',
                                          background: '#f5f5f5',
                                          borderRadius: '8px',
                                        }}
                                        >
                                          <p>视频无法加载</p>
                                          <a href={url} target="_blank" rel="noopener noreferrer">
                                            点击下载
                                          </a>
                                        </div>
                                      </div>
                                    </div>
                                  ))}

                                  {otherUrls.map((url, index) => (
                                    <div
                                      key={`other_${index}`}
                                      style={{
                                        padding: '12px',
                                        background: '#f5f5f5',
                                        borderRadius: '8px',
                                        wordBreak: 'break-all',
                                      }}
                                    >
                                      <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>
                                        {url}
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              );
                            }

                            // Default layout (mixed media): keep the previous "full width per item" rendering.
                            return (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                                {urls.map((url, index) => {
                                  const u = String(url || '');
                                  const isVideo = isVideoUrl(u);
                                  const isImage = isImageUrl(u);
                                  return (
                                    <div key={index} style={{ marginBottom: 16, width: '100%' }}>
                                      {isVideo ? (
                                        <div style={{ position: 'relative', width: '100%', maxWidth: 600 }}>
                                          <video
                                            src={u}
                                            controls
                                            style={{
                                              width: '100%',
                                              maxHeight: '400px',
                                              borderRadius: '8px',
                                              backgroundColor: '#000',
                                            }}
                                            onError={(e) => {
                                              console.error('视频加载失败:', u);
                                              e.target.style.display = 'none';
                                              e.target.nextSibling.style.display = 'block';
                                            }}
                                          />
                                          <div style={{
                                            display: 'none',
                                            padding: '20px',
                                            textAlign: 'center',
                                            background: '#f5f5f5',
                                            borderRadius: '8px',
                                          }}
                                          >
                                            <p>视频无法加载</p>
                                            <a href={u} target="_blank" rel="noopener noreferrer">
                                              点击下载
                                            </a>
                                          </div>
                                        </div>
                                      ) : isImage ? (
                                        <Image
                                          src={u}
                                          alt={`图片 ${index + 1}`}
                                          style={{
                                            maxWidth: '100%',
                                            maxHeight: '400px',
                                            borderRadius: '8px',
                                            objectFit: 'contain',
                                          }}
                                          preview={{ mask: '预览' }}
                                        />
                                      ) : (
                                        <div style={{
                                          padding: '12px',
                                          background: '#f5f5f5',
                                          borderRadius: '8px',
                                          wordBreak: 'break-all',
                                        }}
                                        >
                                          <a href={u} target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff' }}>
                                            {u}
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </Image.PreviewGroup>
                      </div>
                    )}
                    <div style={{ marginBottom: 24 }}>
                      <h3 style={{ marginBottom: 8, fontSize: 18, fontWeight: 600 }}>正文</h3>
                      <div style={{ fontSize: 14, color: '#666', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                        {publishDetail.body || '-'}
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                key: 'info',
                label: '发布信息',
                children: (
                  <div style={{ padding: '16px 0' }}>
                    <div style={{ marginBottom: 16 }}>
                      <h3 style={{ marginBottom: 8, fontSize: 18, fontWeight: 600 }}>基本信息</h3>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>发布ID</div>
                          <div style={{ fontSize: 14, color: '#333' }}>{publishDetail.publish_id}</div>
                        </div>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>内容ID</div>
                          <div style={{ fontSize: 14, color: '#333' }}>{publishDetail.content_id}</div>
                        </div>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>平台</div>
                          <div style={{ fontSize: 14, color: '#333' }}>
                            {platformNameMap[publishDetail.platform] || publishDetail.platform}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>状态</div>
                          <div style={{ fontSize: 14 }}>
                            <Tag color={
                              publishDetail.status === 'success' ? 'green' :
                              publishDetail.status === 'failed' ? 'red' :
                              publishDetail.status === 'pending' ? 'orange' :
                              publishDetail.status === 'verifying' ? 'gold' : 'blue'
                            }>
                              {publishDetail.status === 'success' ? '成功' :
                               publishDetail.status === 'failed' ? '失败' :
                               publishDetail.status === 'pending' ? '待发布' :
                               publishDetail.status === 'verifying' ? '待验证码' : '发布中'}
                            </Tag>
                          </div>
                        </div>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>内容类型</div>
                          <div style={{ fontSize: 14, color: '#333' }}>{publishDetail.template_type || '-'}</div>
                        </div>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>账号ID</div>
                          <div style={{ fontSize: 14, color: '#333' }}>{publishDetail.account_id || '-'}</div>
                        </div>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>账号名</div>
                          <div style={{ fontSize: 14, color: '#333' }}>
                            {publishDetail.account_name || accountNameById[publishDetail.account_id] || '-'}
                          </div>
                        </div>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>发布者ID</div>
                          <div style={{ fontSize: 14, color: '#333' }}>{publishDetail.publisher_user_name || '-'}</div>
                        </div>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>远程帖子ID</div>
                          <div style={{ fontSize: 14, color: '#333' }}>{publishDetail.remote_post_id || '-'}</div>
                        </div>
                      </div>
                    </div>
                    <div style={{ marginBottom: 16 }}>
                      <h3 style={{ marginBottom: 8, fontSize: 18, fontWeight: 600 }}>时间信息</h3>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>发布时间</div>
                          <div style={{ fontSize: 14, color: '#333' }}>{formatDateTime(publishDetail.publish_time)}</div>
                        </div>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>创建时间</div>
                          <div style={{ fontSize: 14, color: '#333' }}>{formatDateTime(publishDetail.created_at)}</div>
                        </div>
                        <div>
                          <div style={{ color: '#999', fontSize: 12, marginBottom: 4 }}>更新时间</div>
                          <div style={{ fontSize: 14, color: '#333' }}>{formatDateTime(publishDetail.updated_at)}</div>
                        </div>
                      </div>
                    </div>
                    {publishDetail.error_code || publishDetail.error_message ? (
                      <div style={{ marginBottom: 16 }}>
                        <h3 style={{ marginBottom: 8, fontSize: 18, fontWeight: 600, color: '#ff4d4f' }}>错误信息</h3>
                        <div style={{ padding: 12, background: '#fff2f0', borderRadius: 4 }}>
                          {publishDetail.error_code && (
                            <div style={{ marginBottom: 8 }}>
                              <span style={{ color: '#999', fontSize: 12 }}>错误代码：</span>
                              <span style={{ color: '#ff4d4f', fontSize: 14 }}>
                                {formatErrorMessage(publishDetail.error_code) || publishDetail.error_code}
                              </span>
                            </div>
                          )}
                          {publishDetail.error_message && (
                            <div>
                              <span style={{ color: '#999', fontSize: 12 }}>错误消息：</span>
                              <span style={{ color: '#ff4d4f', fontSize: 14 }}>
                                {formatErrorMessage(publishDetail.error_message) || publishDetail.error_message}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ),
              },
              {
                key: 'stats',
                label: '数据统计',
                children: (
                  <div style={{ padding: '16px 0' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
                      <div style={{ textAlign: 'center', padding: 24, background: '#f5f5f5', borderRadius: 8 }}>
                        <div style={{ fontSize: 32, fontWeight: 600, color: '#1890ff', marginBottom: 8 }}>
                          {publishDetail.view_count || 0}
                        </div>
                        <div style={{ fontSize: 14, color: '#666' }}>浏览数</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: 24, background: '#f5f5f5', borderRadius: 8 }}>
                        <div style={{ fontSize: 32, fontWeight: 600, color: '#52c41a', marginBottom: 8 }}>
                          {publishDetail.like_count || 0}
                        </div>
                        <div style={{ fontSize: 14, color: '#666' }}>点赞数</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: 24, background: '#f5f5f5', borderRadius: 8 }}>
                        <div style={{ fontSize: 32, fontWeight: 600, color: '#faad14', marginBottom: 8 }}>
                          {publishDetail.comment_count || 0}
                        </div>
                        <div style={{ fontSize: 14, color: '#666' }}>评论数</div>
                      </div>
                      <div style={{ textAlign: 'center', padding: 24, background: '#f5f5f5', borderRadius: 8 }}>
                        <div style={{ fontSize: 32, fontWeight: 600, color: '#722ed1', marginBottom: 8 }}>
                          {publishDetail.share_count || 0}
                        </div>
                        <div style={{ fontSize: 14, color: '#666' }}>分享数</div>
                      </div>
                    </div>
                  </div>
                ),
              },
            ]}
          />
        )}
      </Drawer>

      <Modal
        title="短信验证码"
        open={smsVerifyVisible}
        onCancel={() => {
          setSmsVerifyVisible(false);
          setSmsVerifyInfo(null);
          setSmsVerifyCode('');
          setSmsVerifyPublishId('');
        }}
        footer={(
          <Space>
            <Button onClick={() => {
              setSmsVerifyVisible(false);
              setSmsVerifyInfo(null);
              setSmsVerifyCode('');
              setSmsVerifyPublishId('');
            }}>
              关闭
            </Button>
            <Button
              danger
              onClick={() => handleCancelSmsVerify({ publish_id: smsVerifyPublishId })}
              disabled={smsVerifySubmitting}
            >
              放弃验证码
            </Button>
            <Button
              type="primary"
              onClick={handleSubmitSmsVerify}
              loading={smsVerifySubmitting}
            >
              提交
            </Button>
          </Space>
        )}
        maskClosable={!smsVerifySubmitting}
      >
        {smsVerifyLoading ? (
          <div style={{ padding: '12px 0' }}>正在获取验证码信息…</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ color: '#666', fontSize: 13 }}>
              发布ID：{smsVerifyPublishId || '-'}
            </div>
            <div style={{ color: '#666', fontSize: 13 }}>
              手机号：{smsVerifyInfo?.verification?.phone_mask || '未知'}
            </div>
            {smsVerifyInfo?.verification?.reason && (
              <div style={{ color: '#999', fontSize: 12 }}>
                原因：{smsVerifyInfo?.verification?.reason}
              </div>
            )}
            <Input
              placeholder="请输入短信验证码"
              value={smsVerifyCode}
              onChange={(e) => setSmsVerifyCode(e.target.value)}
              maxLength={8}
            />
            <div style={{ color: '#999', fontSize: 12 }}>
              请在 5 分钟内输入验证码，提交后发布将继续进行。
            </div>
          </div>
        )}
      </Modal>

      {autoLoginAccount && (
        <SocialAccountLoginButton
          key={`auto-login-${autoLoginKey}`}
          account={autoLoginAccount}
          autoStart
          buttonProps={{ style: { display: 'none' } }}
          onSuccess={() => {
            setAutoLoginAccount(null);
            reloadListKeepCurrent().catch(() => {});
          }}
        />
      )}
    </div>
  );
};

export default PublishedList;
